import { useEffect, useState } from "react";
import api from "@/lib/api";
import { DashboardLayout } from "@/components/DashboardLayout";
import { adminNav } from "@/config/nav";
import { StatCard, SectionTitle } from "@/components/ui-bits";
import {
  BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Cell, PieChart, Pie, Tooltip, Legend,
  RadialBarChart, RadialBar,
} from "recharts";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Loader2, Target, Sparkles, FileBarChart } from "lucide-react";

const DIFF_COLOR = { Easy: "#059669", Medium: "#FFB020", Hard: "#E0000F" };

export default function Analytics() {
  const [data, setData] = useState(null);

  useEffect(() => { api.get("/admin/analytics").then((r) => setData(r.data)); }, []);

  if (!data) return <DashboardLayout nav={adminNav} title="Analytics"><div className="py-20 text-center"><Loader2 className="h-6 w-6 animate-spin mx-auto text-primary" /></div></DashboardLayout>;

  const aiTotal = data.ai_vs_manual.reduce((s, x) => s + x.value, 0);

  return (
    <DashboardLayout nav={adminNav} title="Analytics">
      <div className="mb-6">
        <h1 className="font-display text-2xl md:text-3xl font-bold tracking-tight">Platform Analytics</h1>
        <p className="text-muted-foreground mt-1">Performance, difficulty and AI insights.</p>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-px bg-border border border-border mb-8">
        <StatCard label="Published Results" value={data.total_published_results} icon={FileBarChart} index={0} />
        <StatCard label="AI Questions" value={data.ai_vs_manual.find((x) => x.name.includes("AI"))?.value ?? 0} icon={Sparkles} index={1} />
        <StatCard label="Total Bank" value={aiTotal} icon={Target} index={2} />
        <StatCard label="Exams Analyzed" value={data.exam_stats.length} icon={FileBarChart} index={3} />
      </div>

      <div className="grid lg:grid-cols-2 gap-6 mb-8">
        <div className="bg-card border border-border p-6">
          <SectionTitle>Difficulty-Level Accuracy</SectionTitle>
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={data.difficulty_accuracy}>
              <XAxis dataKey="difficulty" tick={{ fontSize: 12 }} axisLine={false} tickLine={false} />
              <YAxis domain={[0, 100]} tick={{ fontSize: 12 }} axisLine={false} tickLine={false} unit="%" />
              <Tooltip cursor={{ fill: "#f3f4f6" }} />
              <Bar dataKey="accuracy" radius={[2, 2, 0, 0]}>
                {data.difficulty_accuracy.map((d) => <Cell key={d.difficulty} fill={DIFF_COLOR[d.difficulty] || "#002FA7"} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>

        <div className="bg-card border border-border p-6">
          <SectionTitle>Question Type Distribution</SectionTitle>
          {data.type_distribution.length === 0 ? (
            <div className="h-[260px] flex items-center justify-center text-muted-foreground text-sm">No data yet</div>
          ) : (
            <ResponsiveContainer width="100%" height={260}>
              <PieChart>
                <Pie data={data.type_distribution} dataKey="value" nameKey="name" outerRadius={90} label>
                  {data.type_distribution.map((_, i) => <Cell key={i} fill={["#002FA7", "#059669", "#FFB020", "#E0000F", "#7c3aed"][i % 5]} />)}
                </Pie>
                <Tooltip />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>

      <div className="bg-card border border-border p-6">
        <SectionTitle>Examination Performance</SectionTitle>
        {data.exam_stats.length === 0 ? (
          <div className="text-muted-foreground text-sm py-8 text-center">No published results yet.</div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Examination</TableHead>
                <TableHead className="text-right">Appeared</TableHead>
                <TableHead className="text-right">Passed</TableHead>
                <TableHead className="text-right">Failed</TableHead>
                <TableHead className="text-right">Pass %</TableHead>
                <TableHead className="text-right">Avg</TableHead>
                <TableHead className="text-right">High</TableHead>
                <TableHead className="text-right">Low</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.exam_stats.map((e) => (
                <TableRow key={e.exam}>
                  <TableCell className="font-medium">{e.exam}</TableCell>
                  <TableCell className="text-right font-mono">{e.appeared}</TableCell>
                  <TableCell className="text-right font-mono text-emerald-600">{e.passed}</TableCell>
                  <TableCell className="text-right font-mono text-destructive">{e.failed}</TableCell>
                  <TableCell className="text-right font-mono">{e.pass_pct}%</TableCell>
                  <TableCell className="text-right font-mono">{e.average}</TableCell>
                  <TableCell className="text-right font-mono">{e.highest}</TableCell>
                  <TableCell className="text-right font-mono">{e.lowest}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>
    </DashboardLayout>
  );
}
